import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { Typography, Button, Paper, CircularProgress } from "@material-ui/core";

export default function BatchStatus() {
    const { jobId } = useParams();
    const [status, setStatus] = useState("checking");
    const [csvUrl, setCsvUrl] = useState(null);
    const [countdown, setCountdown] = useState(10);
    const pageUrl = window.location.href;
    const intervalRef = useRef(null);
    const [plotData, setPlotData] = useState(null);

    useEffect(() => {
        const poll = () => {
            axios.get(`${API_URL}ml_batch_status/${jobId}`)
                .then(res => {
                    if (res.data.status === "done") {
                        clearInterval(intervalRef.current)
                        setStatus("done")
                        const blob = new Blob([res.data.csv], { type: "text/csv" })
                        const url = window.URL.createObjectURL(blob)
                        setCsvUrl(url)
                        if (res.data.plot) setPlotData(res.data.plot)
                    } else {
                        setStatus("pending")
                    }
                })
                .catch(err => {
                    clearInterval(intervalRef.current)
                    if (err.response?.status === 404) setStatus("expired")
                    else setStatus("error")
                })
        }

        poll()
        intervalRef.current = setInterval(poll, 5000)
        return () => clearInterval(intervalRef.current)
    }, [jobId])

    useEffect(() => {
        if (status !== "checking") return;
        if (countdown <= 0) return;
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000)
        return () => clearTimeout(timer)
    }, [countdown, status])

    return (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "5%" }}>
            <Paper elevation={4} style={{ padding: "5% 8%", borderRadius: 10, maxWidth: 600, width: "100%", textAlign: "center" }}>

                <Typography variant="h5" style={{ marginBottom: "5%" }}>
                    Bystander Prediction Job
                </Typography>

                {status === "checking" && (
                    <>
                        <Typography align="center" style={{ marginBottom: "1rem" }}>
                            Checking job status...
                        </Typography>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            {countdown > 0 ? (
                                <>
                                    <Typography align="center">Estimated time:<br/> ~10 seconds</Typography>
                                    <CircularProgress variant="determinate" value={((10 - countdown) / 10) * 100} />
                                    <Typography>{Math.round(((10 - countdown) / 10) * 100)}%</Typography>
                                </>
                            ) : (
                                <>
                                    <CircularProgress />
                                    <Typography>Still checking...</Typography>
                                </>
                            )}
                        </div>
                    </>
                )}

                {status === "pending" && (
                    <>
                        <CircularProgress style={{ marginBottom: "4%" }} />
                        <Typography variant="body1" style={{ marginBottom: "2%" }}>
                            Prediction is running on the cloud.<br />
                            This takes approximately <strong>5–10 minutes</strong>.<br />
                            You can bookmark this link and come back later.
                        </Typography>
                    </>
                )}

                {status === "done" && (
                    <>
                        <Typography variant="body1" style={{ marginBottom: "2%", color: "green" }}>
                            ✓ Prediction complete!
                        </Typography>

                        {plotData && (
                            <div style={{ margin: "1rem 0 2rem 0" }}>
                                <img
                                    src={`data:image/png;base64,${plotData}`}
                                    alt="Bystander prediction results"
                                    style={{ width: "100%", borderRadius: 8 }}
                                />
                            </div>
                        )}

                        <Button
                            variant="contained"
                            style={{ backgroundColor: "#E2EFDA", textTransform: "none" }}
                            href={csvUrl}
                            download={`pridict2_bystander_${jobId}.csv`}
                        >
                            Download Results CSV
                        </Button>
                        <Typography variant="body2" style={{ color: "grey", marginTop: "0.5rem", textAlign: "center" }}>
                            The CSV contains a ranked selection of predicted pegRNA designs. For more bystander options (e.g. 2 flanking amino acids) and the complete set of all designs, run locally via the <a href="https://github.com/uzh-dqbm-cmi/PRIDICT2?tab=readme-ov-file#71-prediction-of-pegrnas-with-silent-bystander-edits" target="_blank" rel="noopener noreferrer" style={{ color: "grey" }}>GitHub repository</a>.
                        </Typography>
                        <Typography variant="body2" style={{ color: "grey", marginTop: "0.8rem" }}>
                            Results are stored for 1 day and then deleted.
                        </Typography>
                    </>
                )}

                {status === "expired" && (
                    <Typography variant="body1" style={{ color: "grey" }}>
                        This prediction is no longer available.<br />
                        Results are stored for 2 days after submission.
                    </Typography>
                )}

                {status === "error" && (
                    <Typography variant="body1" style={{ color: "red" }}>
                        Something went wrong checking the job status.<br />
                        Please try submitting again.
                    </Typography>
                )}

                <Typography
                    variant="body2"
                    style={{ marginTop: "6%", color: "grey", wordBreak: "break-all" }}
                >
                    Bookmark this link to return later:<br />
                    <strong>{pageUrl}</strong>
                </Typography>

            </Paper>
        </div>
    )
}